import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { keyframes, styled, StyledProps } from '../../stitches.config';

const slideUpAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(2px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' },
});

const slideRightAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(-2px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' },
});

const slideDownAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(-2px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' },
});

const slideLeftAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(2px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' },
});

const StyledContent = styled(TooltipPrimitive.Content, {
  'borderRadius': 4,
  'backgroundColor': '$slate2',
  'color': '$slate12',
  '& > p': {
    color: '$slate12',
  },
  'zIndex': 100,
  'padding': '$3',
  'border': '1px solid $slate6',
  'boxShadow': 'hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px',
  'userSelect': 'none',
  'animationDuration': '400ms',
  'animationTimingFunction': 'cubic-bezier(0.16, 1, 0.3, 1)',
  'animationFillMode': 'forwards',
  'maxWidth': '15rem',
  'willChange': 'transform, opacity',
  '&[data-state="delayed-open"], &[data-state="instant-open"]': {
    '&[data-side="top"]': { animationName: slideDownAndFade },
    '&[data-side="right"]': { animationName: slideLeftAndFade },
    '&[data-side="bottom"]': { animationName: slideUpAndFade },
    '&[data-side="left"]': { animationName: slideRightAndFade },
  },
});

const StyledArrow = styled(TooltipPrimitive.Arrow, {
  fill: '$slate10',
  zIndex: 100,
});

// Exports
export const TooltipProvider = TooltipPrimitive.Provider;
export const Tooltip = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;
export const TooltipPortal = TooltipPrimitive.Portal;
export const TooltipContent = StyledContent;
export const TooltipArrow = StyledArrow;

interface SimplifiedTooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  portalProps?: StyledProps<TooltipPrimitive.TooltipPortalProps>;
  contentProps?: StyledProps<TooltipPrimitive.TooltipContentProps>;
  hasArrow?: boolean;
  arrowProps?: StyledProps<TooltipPrimitive.TooltipArrowProps>;
  delayDuration?: number;
}

export function SimplifiedTooltip(props: SimplifiedTooltipProps) {
  const {
    children,
    content,
    portalProps = {},
    contentProps = {},
    hasArrow = true,
    arrowProps = {},
    delayDuration = 200,
  } = props;
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipPortal {...portalProps}>
          <TooltipContent {...contentProps}>
            {hasArrow && <TooltipArrow {...arrowProps} />}
            {content}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
}
